import { Component, OnInit, Output, ViewEncapsulation, EventEmitter, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { AutoSub, AutoSubOnDestroy, GpsState, GyroService, IntroFlowService, MapService, MoteEventService, MoteService, PositionService, ProjectService, QuizService, UserMoteService, UserService, VoucherService, DeviceService } from '../../providers';
import { MotesLoginService } from '../../motes-login'
import { CookieSection, MotesCookieService } from '../../cookie/cookie.service';
import { MediaType, Mote, MoteEvent, MoteEventType, MotesPaths, User } from '../../models';
import { Action, PageKey, Page, PagePosition, WelcomePage, TutorialPage, MapStoryPage, LoginPage, DirectionPage, LocationPage, CookiePage, MapNavigationPage, MapUserPage } from './intro-flow-page.model';


@AutoSubOnDestroy()
@Component({
    selector: 'intro-flow',
    templateUrl: './intro-flow.component.html',
    styleUrls: ['./intro-flow.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class IntroFlowComponent implements OnInit, OnDestroy {

    @Output() login = new EventEmitter<void>();

    public tour: Mote = null;
    public activePage: Page = null;
    public progress: number = 0;
    public activeIcon: string = null;
    public animateIcon: boolean = false;
    public previousIcon: string = null;
    public showGpsHelp: boolean = false;
    public rollBanners: boolean = false;
    public user: User = null;
    public tourStartClicked: boolean = false;
    public pages: Page[] = [];

    private rightStack: Page[] = [];
    private leftStack: Page[] = [];
    private firstMote: Mote = null;
    private tourSpecified: boolean = false;
    private gpsHelpComplete: boolean = false;
    private gpsState: GpsState = null;
    private rightPagePops: number[] = [1];
    private leftPagePops: number[] = [];
    private tourAccess: boolean = true;
    private currentFragment: string = '';
    private moteEvent: MoteEvent = null;
    private sleepTimeout: NodeJS.Timeout = null;

    // Expose to view
    public PagePosition = PagePosition;
    public MediaType = MediaType;

    constructor(public introFlowService: IntroFlowService,
        public location: Location,
        private loginService: MotesLoginService,
        private userService: UserService,
        private userMoteService: UserMoteService,
        private moteService: MoteService,
        private positionService: PositionService,
        private projectService: ProjectService,
        private gyroService: GyroService,
        private quizService: QuizService,
        private moteEventService: MoteEventService,
        private mapService: MapService,
        private cookieService: MotesCookieService,
        private voucherService: VoucherService,
        private deviceService: DeviceService,
        @Inject(LOCALE_ID) public locale: string) {
        // Set all page instances (order in array determines reverse order of appearance)
        this.pages = [
            new TutorialPage(this.introFlowService),
            new MapNavigationPage(this.introFlowService, this.mapService, this.positionService, this.moteEventService),
            new MapStoryPage(this.introFlowService, this.mapService, this.moteEventService),
            new MapUserPage(this.introFlowService, this.mapService, this.positionService, this.moteEventService),
            // new TrailerPage(this.introFlowService),
            // new ChallengePage(this.introFlowService),
            // new GroupPage(this.introFlowService),
            // new GroupPresentationPage(this.introFlowService),
            new LoginPage(),
            new DirectionPage(this.gyroService),
            new LocationPage(this.introFlowService, this.positionService),
            new CookiePage(this.introFlowService),
            new WelcomePage(this.introFlowService)
        ];
    }

    ngOnInit() {

        // Fetch data from intro flow service
        let tour = this.introFlowService.tour;
        this.moteEvent = this.introFlowService.getMoteEvent();
        this.tourStartClicked = this.moteEvent?.type == MoteEventType.START_TOUR || this.moteEvent?.type == MoteEventType.GROUP_START_TOUR;

        // Init directions
        this.gyroService.init();

        // Watch user and update login and group page
        AutoSub(this).reg['userSub'] = this.watchUser(tour);

        // Watch gps state and update location page
        AutoSub(this).reg['gpsSub'] = this.positionService.watchGpsState().subscribe(async (state: GpsState) => {
            if(!state) return;
            this.gpsState = state;
            if(this.activePage?.key == PageKey.locationPage) this.updateActivePage();
        });

        // Watch url page key fragments and update stacks and active page
        AutoSub(this).reg['locationSub'] = this.watchUrl();

        // Set tour properties and init pages
        this.updateTourProperties(tour).then(() => this.initPages());
    }

    private watchUser(tour: Mote): Subscription {
        return this.userService.watchCurrentUser().subscribe(async (user) => {
            this.user = user;
            // Update group page
            if(this.activePage?.key == PageKey.groupPage) this.updateActivePage();
            // Update tour access
            this.tourAccess = (tour) ? await this.getTourAccess(tour) : true;
            // Update login page
            if(this.activePage?.key == PageKey.loginPage) this.updateActivePage();
        });
    }

    private watchUrl(): Subscription {
        return this.introFlowService.watchUrlChange().subscribe((url) => {
            // Get page key from url
            let fragment = url.split('#')[1];
            this.currentFragment = fragment;

            let noIntroFlowShowing = !this.activePage || !this.activePage?.position.includes('active')
            let pageAlreadyShowing = this.activePage?.key == fragment;
            let mapPageChangedRoute = !fragment && this.activePage?.mapPage;
            let videoChangedRoute = fragment && fragment.includes('video');
            if(noIntroFlowShowing || pageAlreadyShowing || mapPageChangedRoute || videoChangedRoute) return;

            // Collect all pages in current flow and get fragment ID
            let stackedPages = this.rightStack.concat([this.activePage]).concat(this.leftStack.reverse());
            let fragmentIdx = stackedPages.findIndex((page) => page.key == fragment);

            // If fragment page is not in stackedPages (user has navigated away from intro flow) => close intro flow
            if(fragmentIdx == -1) {
                this.introFlowService.close();
                return;
            }

            // Fill up right stack until reaching fragment page (avoid swipe animation)
            this.rightStack = stackedPages.slice(0, fragmentIdx);
            this.rightStack.forEach((page) => page.position = PagePosition.place_right);
            // Set fragment page as active page (avoid swipe animation)
            this.activePage = stackedPages[fragmentIdx];
            this.activePage.position = PagePosition.place_active;
            // Fill up left stack with remaining pages (avoid swipe animation)
            this.leftStack = stackedPages.slice(fragmentIdx + 1, stackedPages.length).reverse();
            this.leftStack.forEach((page) => page.position = PagePosition.place_left);

            // Update active page, progress bar and icon
            this.updateActivePage()
            this.updateView();
        });
    }

    private async getTourAccess(tour: Mote): Promise<boolean> {
        if(!tour || tour.price == 0) return true;
        if(!this.user || this.user.isAnonymous) return false;
        return this.voucherService.verifyUserTourAccess(this.user.key, tour, false);
    }

    private async updateTourProperties(tour: Mote) {
        this.tour = tour;
        this.tourSpecified = (tour) ? this.tour.project != MotesPaths.DEFAULT_PROJECT_KEY : false;

        if(this.introFlowService.currentStory) this.firstMote = this.introFlowService.currentStory;
        else (tour) ? this.userMoteService.getCurrentStory(tour).then( first => this.firstMote = first) : null;
    }

    private async initPages() {
        // Fill right stack
        for(let page of this.pages) {
            // Check if page should be included in current flow
            let show = await page.show(this.tour, this.user, this.tourAccess);
            if(show) {
                // Add to right stack
                this.rightStack.push(page);
                page.position = PagePosition.right;
            }
        }

        // If only location page is in intro flow and gps service is initialized => wait 3s to find GPS
        if(this.rightStack.length == 1 && this.rightStack[0].key == PageKey.locationPage && !this.deviceService.isIos()) {
            await this.sleep(3000);
            if(this.gpsState == GpsState.FIRST_VALID || this.gpsState == GpsState.POS_VALID) {
                this.rightStack.pop();
            }
        }

        // If there are no pages in the current flow => close
        if(!this.rightStack.length) {
            await this.closePageLeft();
            return;
        }

        // Set first page
        this.activePage = this.rightStack.pop();
        // Update active page, url fragment, icon and progress bar
        await this.updateActivePage();
        this.activePage.position = PagePosition.active;
        this.navigateForward();
        this.updateView();
        // Notify service that a page is visible
        this.introFlowService.introFlowShowing = true;
    }

    /**
     * Based on the page button action, 
     * set locale storage and determine conditions to move to the next page
     */
    public async handleButtonAction(action: Action) {

        // Save page path in local storage (if applicable)
        await this.activePage.setCookie(this.tour);

        let proceed: boolean = true;
        let pagesToPop = 1;

        switch(action) {
            case Action.LOGIN:
                this.login.emit();
                proceed = false;
                break;
            case Action.LOGOUT:
                // Log out
                await this.loginService.logout();
                proceed = false;
                break;
            case Action.ALLOW_LOCATION:
                // If location is found => proceed
                this.positionService.init();
                if(this.gpsState != GpsState.POS_VALID && this.gpsState != GpsState.FIRST_VALID) proceed = false;
                break;
            case Action.CONTINUE_WITHOUT_LOCATION:
                // Save location ignored for this tour
                this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_LOCATION_IGNORED(this.tour?.key));
                break;
            case Action.HELP_ME_LOCATION:
                // Show GPS help overlay
                this.showGpsHelp = true;
                proceed = false;
                break;
            case Action.RELOAD:
                // Reload page
                if(window) window.location.reload();
                proceed = false;
                break;
            case Action.ALLOW_DIRECTION:
                // Request gyro permission => proceed regardless
                await this.gyroService.requestPermissions();
                break;
            case Action.NO_GROUP:
                // Skip group page
                pagesToPop = 2;
                break;
            case Action.OPEN_COOKIES:
                this.cookieService.showCookieInfo(true, CookieSection.COOKIES);
                proceed = false;
                break;
            case Action.OPEN_TERMS:
                this.cookieService.showCookieInfo(true, CookieSection.TERMS);
                proceed = false;
                break;
            case Action.OPEN_LICENSE:
                this.cookieService.showCookieInfo(true, CookieSection.LICENSE);
                proceed = false;
                break;
            case Action.OPEN_CONTACT:
                window.open(`mailto: ${MotesPaths.SUPPORT_EMAIL}`);
                proceed = false;
                break;
            case Action.FIND_ANOTHER_TOUR:
                // Set active tour to default
                await this.projectService.changeActiveProject(MotesPaths.DEFAULT_PROJECT_KEY, MotesPaths.DEFAULT_TOUR_KEY);
                // Update tour properties
                let defaultTour = await this.moteService.getMote(MotesPaths.DEFAULT_TOUR_KEY, true);
                await this.updateTourProperties(defaultTour);
                // Close flow
                proceed = false;
                this.closePageLeft();
                break;
            case Action.BUY_TOUR: {
                // Set active tour to default
                await this.projectService.changeActiveProject(MotesPaths.DEFAULT_PROJECT_KEY, MotesPaths.DEFAULT_TOUR_KEY);
                proceed = false;
                // Show all tours page
                this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.SHOW_TOURS });
                // Open tour
                setTimeout( () => {
                    this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.OPEN_UNLOCKED_MOTE });
                }, 200);
                break;
            }
            case Action.START_TUTORIAL:
            case Action.USER_ANIMATION_SEEN:
            case Action.STORY_ANIMATION_SEEN:
            case Action.UNLOCK_ANIMATION_SEEN:
            case Action.TOUR_ANIMATION_SEEN:
            case Action.ROUTE_ANIMATION_SEEN:
            case Action.COLLECTION_ANIMATION_SEEN:
                this.updateActivePage(true);
                proceed = false;
                break;
            default: break;
        }
        if(proceed) await this.goToNextPage(pagesToPop);
    }

    public async goToPreviousPage() {
        // Pop as many pages left as was last popped right
        let pagesToPop = this.rightPagePops.pop();
        this.leftPagePops.push(pagesToPop);
        // If there are no more left pages => exit
        if(this.leftStack.length < pagesToPop) {
            await this.closePageRight();
            return;
        }
        // Set new active page
        for(let i = 0; i < pagesToPop; i++) {
            this.rightStack.push(this.activePage);
            this.activePage.position = (i == 0) ? PagePosition.right : PagePosition.place_right;
            this.activePage = this.leftStack.pop();
        };
        // Update active page, url fragment, icon and progress bar
        await this.updateActivePage();
        this.activePage.position = PagePosition.active;
        this.updateView();
    }

    private async goToNextPage(pagesToPop: number) {
        this.rightPagePops.push(pagesToPop);
        // If there are no more right pages => close
        if(this.rightStack.length < pagesToPop) {
            await this.closePageLeft();
            return;
        }
        // Set new active page
        for(let i = 0; i < pagesToPop; i++) {
            this.leftStack.push(this.activePage);
            this.activePage.position = (i == 0) ? PagePosition.left : PagePosition.place_left;
            this.activePage = this.rightStack.pop();
        };
        // Update active page, url fragment, icon and progress bar
        await this.updateActivePage();
        this.activePage.position = PagePosition.active;
        this.navigateForward();
        this.updateView();
    }

    private async updateActivePage(autoUpdateState: boolean = false) {
        await this.activePage.updatePage(this.activePage, this.tour, this.user, this.gpsState, this.activeIcon, this.gpsHelpComplete, this.firstMote, this.tourAccess, autoUpdateState);
        // Update icon if active page icon has changed
        if(this.activeIcon != this.activePage?.icon?.name) {
            this.previousIcon = this.activeIcon;
            this.activeIcon = this.activePage?.icon?.name;
        }
    }

    private async updateView() {
        // Update progress bar
        let pageTotal = this.leftStack.length + this.rightStack.length;
        let pageIndex = this.leftStack.length;
        this.progress = pageTotal > 0 ? (pageIndex / pageTotal * 100) : 0;
        // Add icon animation
        this.animateIcon = true;
        await this.sleep(1000);
        this.animateIcon = false;
    }

    public closeGpsHelp(completed: boolean) {
        this.showGpsHelp = false;
        this.gpsHelpComplete = completed;
        // Simulate gps service initialized
        if(completed) this.activePage.updatePage(this.activePage, this.tour, this.user, GpsState.SERVICE_INITIALIZED, this.activeIcon, this.gpsHelpComplete, this.firstMote, this.tourAccess, false);
    }

    private sleep(ms: number): Promise<void> {
        if(this.sleepTimeout) clearTimeout(this.sleepTimeout);
        return new Promise<void>((resolve) => this.sleepTimeout = setTimeout(() => { resolve() }, ms));
    }

    private async navigateForward() {
        // If there is no page key in url => modify existing url
        if(!this.currentFragment) this.location.replaceState(`${this.location.path()}#${this.activePage.key}`);
        // Otherwise add new url to history (if it is not there already)
        else if(this.currentFragment != this.activePage.key) this.location.go(`${this.location.path()}#${this.activePage.key}`);
    }

    public navigateBack() {
        // Simulate back navigation (browser or phone back button click)
        if(this.progress > 0) this.location.back();
    }

    private async closePageLeft() {
        let introFlowAppeared: boolean = this.activePage != null;
        let activeMapPage: boolean = Boolean(this.activePage?.mapPage);
        let defaultIntroFlow: boolean = !this.tourSpecified && introFlowAppeared;

        // Map pages will take care of opening map and showing what must be shown
        if(!activeMapPage) {

            if(defaultIntroFlow) {
                // Open tours page
                this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.SHOW_TOURS });
            }
            else if(this.tourSpecified) {

                if(this.firstMote) {
                    // Navigate to story
                    this.moteEventService.emitMoteEvent({ type: MoteEventType.NAVIGATE_TO_MOTE, mote: this.firstMote, options: { preventOpenStoryModal: introFlowAppeared } } );
                    // Select story
                    this.moteEventService.emitMoteEvent({ type: MoteEventType.MAP_MOTE_SELECTED, mote: this.firstMote, options: { preventOpenStoryModal: introFlowAppeared } });
                    // Fly to story
                    this.moteEventService.emitMoteEvent({ type: MoteEventType.SHOW_MOTE_ON_MAP, mote: this.firstMote });
                } else {
                    // Fly to tour
                    this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.FLY_TO_TOUR });
                    // Unselect any story
                    this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.HIDE_MAP_MARKER });
                    // Open map
                    this.moteEventService.emitMoteEvent({ mote: this.tour, type: MoteEventType.SHOW_MAP });
                }
            }
        }

        if(activeMapPage) {
            // Wait for banners to roll out
            this.rollBanners = true;
            this.introFlowService.introFlowShowing = false;
            await this.sleep(1000);
        }
        else if(introFlowAppeared) {
            // Wait for page to swipe left
            this.leftStack.push(this.activePage);
            this.activePage.position = PagePosition.left;
            this.introFlowService.introFlowShowing = false;
            await this.sleep(300);
        }

        // Mark flow completed for this tour
        this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_FLOW_COMPLETED(this.tour?.key));
        this.closeIntroFlow();
    }

    public async closePageRight() {
        // Wait for page to swipe right
        if(this.activePage) {
            this.rightStack.push(this.activePage);
            this.activePage.position = PagePosition.right;
            this.introFlowService.introFlowShowing = false;
            await this.sleep(300);
        }

        this.closeIntroFlow();
    }

    private closeIntroFlow() {
        // Remove fragment from url if any
        if(this.location.path(true).includes('#')) this.location.replaceState(this.location.path());
        // Mark (tour-specific) intro flow as seen
        if(this.tourSpecified) this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_FIRST_TOUR_FLOW_SEEN);
        // Close intro flow
        this.introFlowService.close();
    }

    ngOnDestroy(): void {
        if(this.sleepTimeout) clearTimeout(this.sleepTimeout);
    }

}

